<template>
    <div id="main">
        <div class="page" v-show="!rotaAninhada">
            <div class="vertical-flow select-modal">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">
                            {{ $t('propriedades') }}
                            <a class="pl-1" v-if="hide.hideInfo" @click="infoIsHidden()">
                                <b-icon
                                    v-if="!hide.infoIsHidden"
                                    icon="eye-off-outline"
                                    type="is-danger"
                                />
                                <b-icon
                                    v-if="hide.infoIsHidden"
                                    icon="eye-outline"
                                    type="is-success"
                                />
                            </a>
                        </h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">{{ $t('agro') }}</a>
                                </li>
                                <li>
                                    <a class="a-inativo">{{ $t('cadastros') }}</a>
                                </li>
                                <li>
                                    <a aria-current="page">{{ $t('propriedades') }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div class="header-buttons">
                        <button
                            v-if="permissao.create && !propriedadeIntegracao"
                            class="save-btn"
                            type="button"
                            tag="button"
                            @click="$router.push({ name: 'agro-propriedade-new' })"
                        >
                            {{ $t('adicionar') }}
                        </button>
                    </div>
                </div>

                <div class="columns is-multiline">
                    <div class="column">
                        <search-bar :onSearch="debouncedOnSearch" />
                    </div>
                    <!-- SITUACAO -->
                    <div class="situacao-select column is-one-quarter">
                        <label v-text="$t('situacao')" />
                        <v-select
                            ref="situacaoInput"
                            class="is-100"
                            label="label"
                            :options="situacoes"
                            @input="updateSituacao"
                            v-model="situacao"
                        >
                            <template slot="option" slot-scope="option">
                                {{ $t(option.label) }}
                            </template>
                            <template slot="selected-option" slot-scope="option">
                                {{ $t(option.label) }}
                            </template>
                            <template slot="no-options">{{ $t('sem-resultados') }}</template>
                        </v-select>
                    </div>
                </div>

                <div class="registry-wrapper">
                    <empty-table-message v-if="isEmpty" />
                    <b-table
                        v-else
                        class="data-table"
                        :data="propriedades"
                        @sort="updateSort"
                        backend-sorting
                    >
                        <b-table-column
                            v-for="(f, i) in fields"
                            class="td-center"
                            :key="i"
                            :field="f.key"
                            :label="f.label ? $t(f.label) : ''"
                            :visible="
                                f.campo ? campos.length == 0 || campos.includes(f.campo) : true
                            "
                            :sortable="f.sortable"
                            :centered="f.centered"
                            :width="f.width"
                            :numeric="f.numeric"
                            v-slot="props"
                        >
                            <div v-if="f.key == 'situacao'">
                                <b-tag
                                    class="table-tag table-tag-full-width"
                                    :type="props.row.situacao == 1 ? 'is-success' : 'is-danger'"
                                    size="is-medium"
                                >
                                    {{ $t(props.row.situacao == 1 ? 'ativo' : 'inativo') }}
                                </b-tag>
                            </div>
                            <div v-else-if="f.key == 'excluir'">
                                <span v-if="permissao.delete && !propriedadeIntegracao">
                                    <b-button
                                        class="action-button"
                                        :type="
                                            props.row['situacao'] == 1 ? 'is-danger' : 'is-success'
                                        "
                                        :icon-right="
                                            props.row['situacao'] == 1 ? 'delete' : 'check'
                                        "
                                        @click="promptChangeSituacao(props.row)"
                                        v-tippy
                                        :title="
                                            $t(
                                                props.row['situacao'] == 1
                                                    ? 'desativar'
                                                    : 'reativar'
                                            )
                                        "
                                    />
                                </span>
                            </div>
                            <div v-else-if="f.key == 'nome'">
                                <router-link
                                    class="is-bold"
                                    :to="{
                                        name: 'agro-propriedade-edit',
                                        params: {
                                            id: props.row.uuid || props.row.idpropriedadeerp,
                                        },
                                    }"
                                >
                                    <pre>{{ props.row[f.key] }}</pre>
                                </router-link>
                            </div>
                            <div v-else-if="f.key == 'areaTotal'">
                                <span v-if="props.row.areaTotal > 0">
                                    {{ getDecimal(props.row.areaTotal, 2) }}
                                    {{ props.row.unidadeMedida.sigla }}
                                </span>
                                <span v-else> – </span>
                            </div>
                            <div v-else-if="f.key == 'cnpjcpf'">
                                <HideInfo
                                    v-if="!cleanHide"
                                    :value="props.row.cnpjcpf | formatCpfCnpj"
                                    :hideInfo="hide.hideInfo"
                                    :infoIsHidden="hide.infoIsHidden"
                                    showPartInfoPosition="end"
                                    :showPartInfoLenght="4"
                                />
                            </div>
                            <div v-else-if="f.key == 'cliente'">
                                {{ props.row.cliente.rsocial }}
                            </div>

                            <div v-else-if="f.key == 'codigo'">
                                {{ props.row.idpropriedadeerp }}
                            </div>

                            <div v-else>
                                {{ props.row[f.key] }}
                            </div>
                        </b-table-column>
                    </b-table>
                </div>
                <PaginationViewer
                    class="mb-0"
                    :resetPagination="pagination.resetPagination"
                    :totalItems="pagination.totalRows"
                    :perPageOptions="pagination.perPageOptions"
                    :optionStarted="pagination.optionStarted"
                    @change="updatePagination"
                />
                <!-- MODAL -->
                <b-modal :active.sync="showModal" @fecharModal="fecharModal">
                    <div class="bg">
                        <MotivoModal
                            :uuid="toDelete != null ? toDelete.uuid : null"
                            @fecharModal="fecharModal"
                            @save="updateSituacaoRegistro()"
                        />
                    </div>
                </b-modal>
            </div>
        </div>
        <router-view />
    </div>
</template>

<script>
import PropriedadeService from '@/services/agro/PropriedadeService';
import HideInfo from '@/components/shared/HideInfo';
import { mapState } from 'vuex';
import MotivoModal from '@/components/agro/propriedade/modal/ModalMotivo';
import MotivoService from '@/services/agro/MotivoService';

export default {
    beforeRouteUpdate(to, from, next) {
        if (to.name == 'agro-propriedade-new' || to.name == 'agro-propriedade-edit') {
            this.rotaAninhada = true;
            next();
        } else {
            this.rotaAninhada = false;
            this.carregarDados(this.pagination.currentPage);
            next();
        }
    },
    components: {
        HideInfo,
        MotivoModal,
    },
    data() {
        return {
            rotaAninhada: false,
            propriedadeIntegracao: this.$store.state.user.tabelasERPWebServer.includes(
                'PROPRIEDADE'
            ),
            cleanHide: true,
            propriedades: [],
            service: new PropriedadeService(this.$http, this.$router),
            motivoService: new MotivoService(this.$http, this.$router),
            fields: [
                {
                    key: 'codigo',
                    label: 'codigo',
                    campo: 'CODIGO',
                    width: '10%',
                },
                {
                    key: 'nome',
                    label: 'nome',
                    campo: 'NOME',
                    sortable: true,
                    width: null,
                },
                {
                    key: 'cnpjcpf',
                    label: 'cnpj-cpf',
                    campo: 'CNPJCPF',
                    sortable: true,
                    width: null,
                },
                { key: 'cliente', label: 'cliente', sortable: true },
                {
                    key: 'areaTotal',
                    label: 'area-total',
                    sortable: true,
                    numeric: true,
                    width: null,
                },
                {
                    key: 'situacao',
                    label: 'situacao',
                    campo: 'SITUACAO',
                    sortable: true,
                    width: null,
                },
                { key: 'excluir', label: '', sortable: false, width: 20 },
            ],
            situacoes: [
                { label: 'todos', id: 2 },
                { label: 'ativo', id: 1 },
                { label: 'inativo', id: 0 },
            ],
            situacao: { label: 'todos', id: 2 },
            isLoading: false,
            isEmpty: false,
            queryFilter: '',
            // Modal
            showModal: false,
            modalManage: false,
            toDelete: null,
            sort: 'nome',
            order: 'asc',
            campos: this.getCamposVisiveis(),
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            hasMotivo: false,
        };
    },
    created() {
        this.rotaAninhada = this.$route.name != 'agro-propriedade-list';

        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];
        this.permissao = this.hasPermissionAccess('agro', 'agro-cadastros', 'agro-propriedade');
        if (!this.permissao.read) {
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'agro-propriedade' },
            });
        } else {
            this.debouncedOnSearch = this.debounce(this.onSearch, 500);
            this.carregarDados();
            this.loadMotivo();
        }
    },
    computed: {
        ...mapState(['hide']),
    },
    methods: {
        loadMotivo() {
            let params = ['.*', 'origem', 'PROPRIEDADE_INATIVA'];
            this.motivoService
                .findAllCombo(params)
                .then((body) => {
                    return body.content.map((m) => {
                        return { id: m.id };
                    });
                })
                .then((r) => {
                    this.hasMotivo = r.length > 0;
                })
                .catch((e) => {
                    console.error(`Erro`, e);
                });
        },
        infoIsHidden() {
            this.$store.dispatch('infoIsHidden', {
                infoIsHidden: !this.hide.infoIsHidden,
            });
        },
        updatePagination(value) {
            this.cleanHide = true;
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1) {
                this.pagination.resetPagination = true;
            }
            this.isLoading = true;
            this.service
                .findAll(
                    this.sort,
                    this.order,
                    currentPage - 1,
                    this.pagination.perPage,
                    this.situacao.id,
                    this.queryFilter
                )
                .then((r) => {
                    this.pagination.totalRows = r.totalElements;
                    this.propriedades = r.content;
                    this.isEmpty = this.propriedades.length <= 0;
                })
                .catch((e) => {
                    console.error('Erro ao carregar propriedades.', e);
                    this.isEmpty = true;
                    this.$root.showMessage('error', 'msg-erro-dados-geral');
                })
                .finally((_) => {
                    this.cleanHide = false;
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        onSearch(value) {
            this.queryFilter = this.removeAcentos(value);
            this.queryFilter = this.queryFilter.replaceAll(/[./-]/g, '');
            this.carregarDados();
        },
        promptChangeSituacao(row) {
            this.toDelete = row;

            let message = this.$t(
                    row.situacao == 1
                        ? 'msg-confirmacao-desativacao-n'
                        : 'msg-confirmacao-reativacao-n', 
                    { n: row.nome }
                );
            this.$root.confirm({
                message: message,
                onConfirm: () => {
                    row.situacao == 0 || !this.hasMotivo
                        ? this.updateSituacaoRegistro()
                        : (this.showModal = true);
                },
                onCancel: () => (this.toDelete = null),
            });
        },
        updateSituacaoRegistro() {
            this.showModal = false;
            this.isLoading = true;
            this.service
                .changeSituacao(this.toDelete.uuid)
                .then((r) => {
                    // Aviso dinâmico passado o nome do usuário
                    let tempSuccess = this.$t(
                        this.toDelete.situacao == 1
                            ? 'msg-sucesso-ao-reativar-n'
                            : 'msg-sucesso-ao-desativar-n', 
                        { n: this.toDelete.nome }
                    );

                    this.$root.showMessage('success', tempSuccess, true, false);
                    this.carregarDados(this.pagination.currentPage);
                })
                .catch((e) => {
                    console.error('Erro ao remover propriedade.', e);
                    // novo modelo de mensagem dinâmica
                    let msg = this.$t(
                        this.toDelete.situacao == 0
                            ? 'msg-erro-ao-desativar-n'
                            : 'msg-erro-ao-reativar-n', 
                        { n: this.toDelete.nome }
                    );
                    this.$root.showMessage('error', msg, true, false);
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.toDelete = null;
                });
        },
        getCamposVisiveis() {
            let modulo = this.getModulo('PROPRIEDADE');
            let campos = [];
            if (modulo) {
                campos = modulo.detalhe.filter((e) => !e.oculta).map((e) => e.campo);
            }
            campos.push('CODIGO', 'SITUACAO');
            return campos;
        },
        updateSituacao(t) {
            if (t) {
                this.situacao = t;
            } else {
                this.situacao = this.situacoes[0];
            }
            this.carregarDados();
        },
        updateSort(sort, order) {
            this.cleanHide = true;
            this.sort = sort;
            this.order = order;
            this.carregarDados();
        },
        fecharModal() {
            this.showModal = false;
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: 100%;

    h1.title {
        padding-top: 0px;
        margin-bottom: 0 !important;
    }

    .page {
        position: relative;
        padding-left: 0;

        .situacao-select div {
            height: 40px;
        }
    }
}
</style>
